/**
 * PrintablePlanView — versión limpia del plan, solo para imprimir / guardar PDF.
 *
 * Renderiza las secciones con `<SectionView readonly>` (el mismo render que ve
 * el cliente en el link público): sin botones de "agregar ejercicio/alimento",
 * sin íconos de editar, sin inputs — solo el contenido.
 *
 * En pantalla está oculto (`.al-print-root { display: none }`). Vive en un
 * portal a `<body>` para que `@media print` pueda esconder `#root` y mostrar
 * solo esto. Los saltos de página los maneja el navegador con las reglas
 * `@media print` de styles.css (nada se corta a la mitad).
 */
import { createPortal } from "react-dom";
import { type CSSProperties } from "react";
import type { PlanDoc, PlanSection } from "../../types/plan";
import { SectionView } from "./sections/SectionView";
import { fontStack } from "../../lib/fontStack";
import { HEALTH_DISCLAIMER_CLIENT } from "../../lib/HealthDisclaimer";

export function PrintablePlanView({ plan }: { plan: PlanDoc }) {
  const brand = plan.brand;
  const isDark = brand?.theme === "dark";
  const primary = brand?.primary || "#7c3aed";
  const accent = brand?.accent || "#ec4899";
  const densityClass =
    brand?.density === "compact"
      ? "al-density-compact"
      : brand?.density === "spacious"
      ? "al-density-spacious"
      : "";

  const cssVars: CSSProperties = {
    ["--al-primary" as any]: primary,
    ["--al-accent" as any]: accent,
    ["--primary" as any]: primary,
    ["--accent-magenta" as any]: accent,
    ["--al-bg" as any]: isDark ? "#0a0a0a" : "#ffffff",
    ["--al-ink" as any]: isDark ? "#f4f4f5" : "#0a0a0a",
  };
  if (brand?.displayFont) (cssVars as any)["--font-display"] = fontStack(brand.displayFont);
  if (brand?.bodyFont) (cssVars as any)["--font-body"] = fontStack(brand.bodyFont);

  const sections = (plan.sections || []).filter(
    (s): s is PlanSection => !!s && s.enabled !== false,
  );
  const noop = () => {};

  return createPortal(
    <div
      className={
        "al-print-root" +
        (isDark ? " al-plan-dark" : "") +
        (densityClass ? " " + densityClass : "")
      }
      style={cssVars}
      aria-hidden="true"
    >
      {sections.map((s) => (
        <div className="al-print-page" key={s.id}>
          <SectionView section={s} brand={plan.brand} onChange={noop} readonly />
        </div>
      ))}
      {/* Aviso de salud al cierre del PDF (política Google Play). Hoja propia
          para que el cliente lo vea sin que se corte con el contenido del plan. */}
      <div className="al-print-page al-print-disclaimer-page" key="health-disclaimer">
        <div className="al-print-disclaimer">
          <div className="al-print-disclaimer-title">Aviso importante</div>
          <p className="al-print-disclaimer-body">{HEALTH_DISCLAIMER_CLIENT}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
